// src/pages/B2B/event-reports/components/ReportImageUpload.tsx
import React, { useState } from 'react';
import type { Env } from '@/pages/Admin/shared';
import { useAdminApi } from '@/pages/Admin/shared/adminApi';

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = new Set(['image/jpeg', 'image/png']);

interface Props {
  env: Env;
  value: { staging_key: string | null; preview_url: string | null };
  onChange: (next: { staging_key: string | null; preview_url: string | null }) => void;
}

export function ReportImageUpload({ env, value, onChange }: Props): React.ReactElement {
  const { request } = useAdminApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    if (!ALLOWED.has(file.type)) {
      setError('Only JPEG or PNG');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Max 10 MB');
      return;
    }

    setLoading(true);
    try {
      const { upload_url, staging_key } = await request<{ upload_url: string; staging_key: string }>({
        env,
        path: '/admin/event-reports/upload-presign',
        method: 'POST',
        body: { filename: file.name, content_type: file.type },
      });

      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putRes.ok) throw new Error(`upload failed: HTTP ${putRes.status}`);

      const previewUrl = URL.createObjectURL(file);
      onChange({ staging_key, preview_url: previewUrl });
    } catch (e: any) {
      setError(e?.message ?? 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const clear = () => onChange({ staging_key: null, preview_url: null });

  return (
    <div className="flex items-start gap-4">
      <div className="w-32 h-32 rounded-lg overflow-hidden border border-gray-700 bg-gray-900 flex items-center justify-center">
        {value.preview_url ? (
          <img src={value.preview_url} alt="cover" className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs text-gray-600">no image</span>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <label className="px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 hover:bg-gray-700 cursor-pointer w-fit">
          {loading ? 'Uploading…' : value.staging_key ? 'Replace image' : 'Upload image'}
          <input
            type="file"
            accept="image/jpeg,image/png"
            className="hidden"
            disabled={loading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              e.target.value = '';
            }}
          />
        </label>
        {value.staging_key && (
          <button
            type="button"
            onClick={clear}
            className="px-3 py-1.5 text-xs text-gray-400 border border-gray-700 rounded-lg hover:text-white hover:bg-gray-800 w-fit"
          >
            Remove
          </button>
        )}
        {error && <p className="text-xs text-red-400">{error}</p>}
        <p className="text-xs text-gray-500">JPEG/PNG, max 10 MB. Optional.</p>
      </div>
    </div>
  );
}

export default ReportImageUpload;
